import { BaseSyntheticEvent } from 'react'
import { AppConstantes } from '../../../../utils/Constantes'
import { Tarea } from '../../../tareas/models'
import { Condicionante } from '../../models'

type Props = {
  arregloObjetos: any
  setArregloObjetosFiltrados: (arg: any) => void
  tipo: string
  setFiltroActivo?: (arg: boolean) => void
  borrarFiltroTarea?: () => void
  palabraInputBusqueda: string
  setPalabraInputBusqueda: (arg: string) => void
}

/**
 * @description Filtra un arreglo de objetos de acuerdo
 *              a ciertos atributos.
 *
 * @author ana.carrasco  Hábil MX
 * @version 1.0.0, 28/02/2023
 * @param param0 arregloObjetos: arreglo sobre el cual se quiere
 *                              aplicar los filtros
 *        setArregloObjetosFiltrados: funcion hacia un estado que
 *                                   devuelve los objetos que se
 *                                   filtraron
 *        tipo: 'condicionante' o 'tarea' para poder aplicar filtro a
 *              determinados atributos
 *        setFiltroActivo: Función hacia un estado que indica cuando
 *                         se ha realizado una busqueda en el input
 *                         en este caso aplicado unicamente al tipo
 *                         'tarea'
 *        borrarFiltrosTarea: activa la funcion definida en componente
 *                         de tareas para poder borrar el input al
 *                         cambiar de selección o al tachar en el propio
 *                          input
 *        palabraInputBusqueda: estado que permite colocar la palabra
 *                         ingresada como valor en el input.
 *       setPalabraBusqueda: estado que permite actualizar lo escrito
 *                          en el input y así poder borrarlo también
 *                          con la función borrar.
 *
 * @returns el componente de input para buscar por filtros de acuerdo
 *          al array de objetos ingresado
 */
export const InputBusquedaFiltrado = ({
  arregloObjetos,
  setArregloObjetosFiltrados,
  tipo,
  setFiltroActivo,
  borrarFiltroTarea,
  palabraInputBusqueda,
  setPalabraInputBusqueda,
}: Props) => {
  /**
   * @description Filtra la condicionante por nombre, descripcion y estatus
   *
   * Nota: el filtrado es "contains" y realiza variantes manuales de mayúsculas/minúsculas.
   * No identificado en el codigo/configuracion revisada: requisitos de i18n/acentos.
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 21/02/2023
   * @param palabraBusqueda String ingresado en el input de búsqueda
   */
  const filtrarObjetos = (palabraBusqueda: BaseSyntheticEvent) => {
    const oracionEntrada = palabraBusqueda.target.value.toString()
    const palabra = palabraBusqueda.target.value.toString()
    const palabraMayusculas = palabraBusqueda.target.value.toString().toLowerCase()
    const palabraMinusculas = palabraBusqueda.target.value.toString().toUpperCase()
    const palabraPrimeraMayusculas = palabraBusqueda.target.value
      .toString()
      .charAt(0)
      .toUpperCase()
      .concat(palabra.slice(1, palabra.length).toLowerCase())
    const oracion = oracionEntrada.split(' ')
    for (let i = 0; i < oracion.length; i++) {
      if (oracion[i][0]) {
        oracion[i] = oracion[i][0].toUpperCase() + oracion[i].substr(1)
      }
    }
    const oracionPrimeraMayusculas = oracion.join(' ')

    const objetosFiltrados =
      tipo === AppConstantes.CVE_INPUT_TAREA
        ? arregloObjetos.filter(
            (e: Tarea) =>
              // Se filtra por título/usuario/descripcion (sin normalización por acentos).
              e.titulo.includes(palabra) ||
              e.titulo.includes(palabraMayusculas) ||
              e.titulo.includes(palabraMinusculas) ||
              e.titulo.includes(palabraPrimeraMayusculas) ||
              e.titulo.includes(oracionPrimeraMayusculas) ||
              e.nombreUsuarioAsignado?.includes(palabra) ||
              e.nombreUsuarioAsignado?.includes(palabraMinusculas) ||
              e.nombreUsuarioAsignado?.includes(palabraMayusculas) ||
              e.nombreUsuarioAsignado?.includes(palabraPrimeraMayusculas) ||
              e.descripcion?.includes(palabra) ||
              e.descripcion?.includes(palabraMinusculas) ||
              e.descripcion?.includes(palabraMayusculas) ||
              e.descripcion?.includes(palabraPrimeraMayusculas)
          )
        : tipo === AppConstantes.CVE_INPUT_CONDICIONANTE &&
          arregloObjetos.filter(
            (e: Condicionante) =>
              // Se filtra por nombre/descripcion/descEstatus.
              e.nombre.includes(palabra) ||
              e.nombre.includes(palabraMayusculas) ||
              e.nombre.includes(palabraMinusculas) ||
              e.nombre.includes(palabraPrimeraMayusculas) ||
              e.nombre.includes(oracionPrimeraMayusculas) ||
              e.descripcion.includes(palabra) ||
              e.descripcion.includes(palabraMayusculas) ||
              e.descripcion.includes(palabraMinusculas) ||
              e.descripcion.includes(palabraPrimeraMayusculas) ||
              e.descripcion.includes(oracion) ||
              e.descEstatus.includes(palabra) ||
              e.descEstatus.includes(palabraMinusculas) ||
              e.descEstatus.includes(palabraMayusculas) ||
              e.descEstatus.includes(palabraPrimeraMayusculas)
          )

    setArregloObjetosFiltrados(objetosFiltrados)
  }

  /**
   * @description borra la palabra ingresada en la busqueda de condicionantes
   *
   * @author ana.carrasco  Hábil MX
   * @version 1.0.0, 21/02/2023
   */
  const borrarFiltro = () => {
    setPalabraInputBusqueda('')
    setFiltroActivo && setFiltroActivo(false)
  }

  return (
    <div className='card-header justify-content-between  pe-3 px-3' style={{ height: '70px' }}>
      <div className='mt-0  col-12'>
        <div className='d-flex align-items-center position-relative my-1'>
          <span className='svg-icon svg-icon-1 position-absolute ms-3'>
            <svg
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'>
              <rect
                opacity='0.5'
                x='17.0365'
                y='15.1223'
                width='8.15546'
                height='2'
                rx='1'
                transform='rotate(45 17.0365 15.1223)'
                fill='currentColor'
              />
              <path
                d='M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z'
                fill='currentColor'
              />
            </svg>
          </span>

          <input
            className='form-control form-control-solid w-450px ps-14'
            maxLength={100}
            type='text'
            value={palabraInputBusqueda}
            placeholder='Buscar...'
            onChange={(value: BaseSyntheticEvent) => {
              filtrarObjetos(value)
              setPalabraInputBusqueda(value.target.value)
              setFiltroActivo && setFiltroActivo(true)
            }}
          />
          <button
            type='button'
            className='react-datepicker__close-icon me-3'
            onClick={() => {
              if (tipo === AppConstantes.CVE_INPUT_CONDICIONANTE) {
                borrarFiltro()
              }
              setArregloObjetosFiltrados(arregloObjetos)

              if (tipo === AppConstantes.CVE_INPUT_TAREA && borrarFiltroTarea) {
                borrarFiltroTarea()
              }
            }}
          />
        </div>
      </div>
    </div>
  )
}
